import { createContext, FC, useState, useContext, useEffect } from "react";
import { prefetchApiCsrfToken } from "api/common";
import { ApiSignIn } from "api/auth/signIn";
import { ApiLogOut } from "api/auth/logOut";
import Cookies from "universal-cookie";
import * as Types from "../api/types";
import { useNavigate, Navigate, Outlet } from "react-router-dom";
import routes from "routes";
import { useAuthStore, type AuthState } from "stores/authStore";
import { AUTH_GROUPS } from "config/authRegistry";

const AuthContext = createContext<Types.AuthContextType | null>(null);
const cookies = new Cookies();

function readUserFromCookie(): Types.User | null {
  const userCookie = cookies.get("millenials-user");
  if (!userCookie) {
    return null;
  }
  return new Types.User(
    userCookie.id,
    userCookie.username,
    userCookie.first_name,
    userCookie.last_name,
    userCookie.department,
    userCookie.groups,
    userCookie.phone_number,
    userCookie.is_staff,
    userCookie.is_active,
    userCookie.email,
    userCookie.user_permissions,
    userCookie.last_login,
    userCookie.deleted_at
  );
}

const AuthProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<Types.User | null>(() => readUserFromCookie());
  const [error, setError] = useState<Types.LoginError | null>(null);
  const navigate = useNavigate();
  const resetAuthStore = useAuthStore((s: AuthState) => s.reset);
  const fetchMe = useAuthStore((s: AuthState) => s.fetchMe);
  const refreshAccessToken = useAuthStore((s: AuthState) => s.refreshAccessToken);
  const setAccessToken = useAuthStore((s: AuthState) => s.setAccessToken);

  useEffect(() => {
    if (!user) {
      return;
    }
    prefetchApiCsrfToken().catch(() => {});
  }, [user?.id]);

  /** Tras login o recarga: JWT en cookie (refresh) + /auth/me en Zustand. */
  useEffect(() => {
    if (!user) {
      resetAuthStore();
      return;
    }
    let cancelled = false;
    (async () => {
      await refreshAccessToken().catch(() => {});
      if (cancelled) {
        return;
      }
      await fetchMe();
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user, resetAuthStore, refreshAccessToken, fetchMe]);

  const loginAction = async (loginData: Types.iLoginData) => {
    setError(null);
    try {
      const response = await ApiSignIn(loginData);
      const data = response.data as Record<string, unknown>;
      const access = typeof data.access === "string" ? data.access : undefined;
      const userForCookie = { ...data };
      delete userForCookie.access;
      delete userForCookie.refresh;
      const normalized = userForCookie as unknown as Types.User & {
        department: Types.Group;
      };
      normalized.department = {
        id: normalized.department.id,
        name: normalized.department.name,
      };
      if (access) {
        setAccessToken(access);
      }
      cookies.set("millenials-user", JSON.stringify(normalized), {
        sameSite: "strict",
        maxAge: 1209600,
      });
      setUser(
        new Types.User(
          normalized.id,
          normalized.username,
          normalized.first_name,
          normalized.last_name,
          normalized.department,
          normalized.groups,
          normalized.phone_number,
          normalized.is_staff,
          normalized.is_active,
          normalized.email,
          normalized.user_permissions,
          normalized.last_login,
          normalized.deleted_at
        )
      );
      await prefetchApiCsrfToken().catch(() => {});
      navigate("/home/default");
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { non_field_errors?: string[] } } })?.response?.data
          ?.non_field_errors?.[0] ?? "Error de inicio de sesión";
      setError({ message });
    }
  };

  const logoutAction = async (expiredSession: boolean = false) => {
    if (!expiredSession) {
      await ApiLogOut().then().catch(() => {});
    }
    cookies.remove("millenials-user");
    cookies.remove("millenials-user-simulating");
    cookies.remove("millenials-viewing-dashboards");
    resetAuthStore();
    setUser(null);
    navigate("/auth/sign-in");
  };

  const isSimulating = cookies.get("millenials-user-simulating");
  const setSimulation = (value: boolean) => {
    cookies.set("millenials-user-simulating", value);
  };

  const lastNotificationReview = cookies.get("millenials-last-notification-review");
  const setLastReview = (value: Date) => {
    cookies.set("millenials-last-notification-review", value);
  };

  const userActions = {
    simulating: { value: isSimulating ? isSimulating : false, change: setSimulation },
    notifications: {
      value: lastNotificationReview ? lastNotificationReview : null,
      change: setLastReview,
    },
  };

  return (
    <AuthContext.Provider
      value={{ user, userActions, error, loginAction, logoutAction }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth debe usarse dentro de AuthProvider");
  }
  return ctx;
};

export const PrivateRoute = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth/sign-in" />;
  return <Outlet />;
};

export const DefaultRoute = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth/sign-in" />;
  const firstModuleWithAccess = user.getFirstModuleWithAccess();
  const defaultRoute = routes.filter((r) =>
    r.moduleName ? r.moduleName === firstModuleWithAccess : false
  )[0];
  return <Navigate to={`${defaultRoute.layout}${defaultRoute.path}`} />;
};

export const userIsProcura = (user: Types.User | null): boolean =>
  Boolean(user?.groups?.some((g) => g.name === AUTH_GROUPS.PROCURA));

export const ProcuraOnlyRoute = ({
  redirectTo = "/coverage/simulator",
}: {
  redirectTo?: string;
}) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth/sign-in" />;
  if (!userIsProcura(user)) return <Navigate to={redirectTo} replace />;
  return <Outlet />;
};
